/**
 * Gift options module.
 * Keeps the Add to Cart / payment buttons blocked until the required
 * delivery date and gift note have been filled in.
 */
class ProductGiftOptionsModule {
  static DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  constructor(controller) {
    this.controller = controller;
    this.section = controller.section;
    this.root = controller.query('[data-gift-options]');
    this.form = controller.query('[data-product-form]');
    this.submitButton = controller.query('[data-submit-button]');
    this.paymentButtonWrapper = controller.query('.product-form__payment-button');
    this.variantSelect = controller.query('[data-variant-select]');

    if (!this.root || !this.form || !this.submitButton) return;

    this.fields = Array.from(this.root.querySelectorAll('[data-gift-field]'));
    this.formError = this.root.querySelector('[data-gift-form-error]');
    this.showErrors = false;
    this.hintOnLoad = this.root.dataset.hintOnLoad !== 'false';
    this.variantAvailable = this.readVariantAvailability(null, !this.submitButton.disabled);

    this.handleSubmitCapture = this.handleSubmitCapture.bind(this);
    this.handlePaymentClickCapture = this.handlePaymentClickCapture.bind(this);
    this.handleBlockedActionsClick = this.handleBlockedActionsClick.bind(this);

    this.bindEvents();
    this.fields.forEach((field) => this.updateCounter(field));
    this.apply();
  }

  bindEvents() {
    this.fields.forEach((field) => {
      field.addEventListener('input', () => {
        this.updateCounter(field);
        if (this.validateField(field).valid) this.setFieldError(field, '');
        this.apply();
      });

      field.addEventListener('change', () => {
        // A blocked weekday can only be caught after the picker commits a value.
        const result = this.validateField(field);
        this.setFieldError(field, result.valid ? '' : result.message);
        this.apply();
      });

      field.addEventListener('blur', () => {
        const result = this.validateField(field);
        this.setFieldError(field, result.valid ? '' : result.message);
      });
    });

    document.addEventListener('submit', this.handleSubmitCapture, true);

    // A disabled button fires no click, so listen on its wrapper to explain why.
    this.submitButton.closest('.product-form__actions')?.addEventListener('click', this.handleBlockedActionsClick);
    this.paymentButtonWrapper?.addEventListener('click', this.handlePaymentClickCapture, true);

    this.section.addEventListener('variant:change', (event) => {
      window.setTimeout(() => {
        this.variantAvailable = this.readVariantAvailability(event.detail?.variantOption, this.variantAvailable);
        this.apply();
      }, 0);
    });

    document.addEventListener('cart:added', () => {
      window.setTimeout(() => this.apply(), 0);
    });

    if ('MutationObserver' in window) {
      this.submitObserver = new MutationObserver(() => this.reassertBlockedState());
      this.submitObserver.observe(this.submitButton, { attributes: true, attributeFilter: ['disabled'] });
    }
  }

  readVariantAvailability(variantOption, fallback) {
    const option = variantOption || this.variantSelect?.selectedOptions?.[0];
    if (!option) return fallback;
    return option.dataset.variantAvailable !== 'false';
  }

  validateField(field) {
    const value = (field.value || '').trim();
    const required = field.dataset.giftRequired === 'true';

    if (!value) {
      return required
        ? { valid: false, message: field.dataset.giftField === 'date' ? 'Please choose a delivery date.' : 'Please add your gift note.' }
        : { valid: true, message: '' };
    }

    if (field.dataset.giftField === 'date') {
      if (field.min && value < field.min) {
        return { valid: false, message: 'Please choose a later delivery date.' };
      }
      if (field.max && value > field.max) {
        return { valid: false, message: 'Please choose an earlier delivery date.' };
      }

      const blocked = this.blockedWeekdays(field);
      if (blocked.length) {
        // Parsed as local time; `new Date('YYYY-MM-DD')` would be UTC and can shift a day.
        const [year, month, day] = value.split('-').map(Number);
        const weekday = new Date(year, month - 1, day).getDay();

        if (blocked.includes(weekday)) {
          return {
            valid: false,
            message: `We do not deliver on ${ProductGiftOptionsModule.DAY_NAMES[weekday]}s. Please choose another date.`,
          };
        }
      }
    }

    return { valid: true, message: '' };
  }

  blockedWeekdays(field) {
    return (field.dataset.giftBlockedWeekdays || '')
      .split(',')
      .map((day) => parseInt(day, 10))
      .filter((day) => day >= 0 && day <= 6);
  }

  isValid() {
    return this.fields.every((field) => this.validateField(field).valid);
  }

  setFieldError(field, message) {
    const errorElement = field.parentElement?.querySelector('[data-gift-error]');

    field.classList.toggle('product-gift-options__input--invalid', Boolean(message));
    field.setAttribute('aria-invalid', message ? 'true' : 'false');

    if (!errorElement) return;
    errorElement.textContent = message;
    errorElement.hidden = !message;
  }

  updateCounter(field) {
    const counter = field.parentElement?.querySelector('[data-gift-counter]');
    if (!counter || !(field.maxLength > 0)) return;

    counter.textContent = `${field.value.length} / ${field.maxLength} characters`;
  }

  apply() {
    const valid = this.isValid();

    this.blocked = !valid;
    this.submitButton.dataset.giftBlocked = valid ? 'false' : 'true';

    if (!this.submitButton.classList.contains('loading')) {
      this.submitButton.disabled = !valid || !this.variantAvailable;
    }

    this.paymentButtonWrapper?.classList.toggle('product-form__payment-button--gift-blocked', !valid);
    this.paymentButtonWrapper?.setAttribute('aria-disabled', valid ? 'false' : 'true');

    if (this.formError) {
      this.formError.hidden = valid || !(this.showErrors || this.hintOnLoad);
      this.formError.classList.toggle('product-gift-options__form-error--hint', !this.showErrors);
      this.formError.setAttribute('role', this.showErrors ? 'alert' : 'status');
    }

    if (valid) {
      this.showErrors = false;
      this.fields.forEach((field) => this.setFieldError(field, ''));
    }
  }

  showValidationMessages() {
    this.showErrors = true;
    this.fields.forEach((field) => {
      const result = this.validateField(field);
      this.setFieldError(field, result.valid ? '' : result.message);
    });
    this.apply();
    window.CartApi?.showToast(this.formError?.textContent.trim() || 'Please complete the fields above before adding to cart.', true);

    const firstInvalid = this.fields.find((field) => !this.validateField(field).valid);
    firstInvalid?.focus();
    firstInvalid?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  reassertBlockedState() {
    if (this.submitButton.classList.contains('loading')) return;
    if (this.isValid() || this.submitButton.disabled) return;

    this.submitButton.disabled = true;
  }

  handleBlockedActionsClick(event) {
    if (this.isValid() || event.target.closest('.product-form__payment-button, .product-form__quantity')) return;

    this.showValidationMessages();
  }

  handleSubmitCapture(event) {
    if (event.target !== this.form || this.isValid()) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.showValidationMessages();
  }

  handlePaymentClickCapture(event) {
    if (this.isValid()) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    this.showValidationMessages();
  }
}

window.HerbiProductSection.registerModule('gift-options', (controller) => {
  return new ProductGiftOptionsModule(controller);
});
